import { GameContext } from '../../../contexts/GameContext';
import { ImageWrapper } from '../../../components/common/wrappers/ImageWrapper';
import { ProfileContext } from '../../../contexts/ProfileContext';
import { ZdarGameStepConfiguration } from '../../../configuration/games/zdar-escape-game/zdarEscapeGameContentConfiguration';
import { isEmptyObject } from '../../../util/isEmptyObject';
import { useContext, useState } from 'react';
import { useTime } from '../../../hooks/useTime';
import ContinueButton from '../../../components/common/buttons/ContinueButton';
import GameAudioPlayer from '../../../components/common/GameAudioPlayer';
import GameWrapper from '../../../components/common/wrappers/GameWrapper';
import Loading from '../../../components/common/Loading';
import ZdarTaskText from '../../../components/games/zdar-escape-game/ZdarTaskText';
import ZdarTaskTitle from '../../../components/games/zdar-escape-game/ZdarTaskTitle';
import cat from '../../../assets/images/games/zdar-escape-game/cats/cat-run.webp';
import facebook from '../../../assets/icons/facebook.svg';
import image from '../../../assets/images/games/zdar-escape-game/coats/erb1.webp';
import instagram from '../../../assets/icons/instagram.svg';
import logo from '../../../assets/images/common/logo.webp';
import parse from 'html-react-parser';
import useLocalStorage from '../../../hooks/useLocalStorage';
import website from '../../../assets/icons/website.svg';

type Props = {
    title?: string;
    currentStep: number;
};

export default function ZdarEnding(props: Props) {
    const { availableGame } = useContext(ProfileContext);
    const { getTimeSplit } = useTime();
    const [page, setPage] = useLocalStorage('sifrovka-endingPage', 1);
    const giftCertificateText = `
        Pro ty, kteří dostali Šifrovku skrze dárkový poukaz, máme jednu malou zajímavost. 
        Všimli jste si, že už tam <b>chybí erb</b> Žďáru? Kdo by nevěděl, podívejte se na poukaz na <b>fotografii staré radnice.</b>`;
    const thankYouText = `
        Na závěr bychom rádi poděkovali všem, kteří nám více či méně v rámci této šifrovací únikové hry pomohli. 
        V první řadě děkujeme <b>Daliboru Bušovi</b>, který propůjčil hlas hlavnímu hrdinovi tohoto příběhu. 
        Dále bychom rádi poděkovali <b>Masu Žďársko</b>. Děkujeme.
    `;
    const [audioOpened1, setAudioOpened1] = useState(false);
    const [audioOpened2, setAudioOpened2] = useState(false);
    const [audioOpened3, setAudioOpened3] = useState(false);
    const [audioOpened4, setAudioOpened4] = useState(false);
    const [audioOpened5, setAudioOpened5] = useState(false);
    const [audioOpened6, setAudioOpened6] = useState(false);

    const { stepContent } = useContext(GameContext);
    const { taskText2, coatTexts, coatImages, audio, taskText } = stepContent as ZdarGameStepConfiguration;

    const onOpenAudio = (value: boolean, func: any) => {
        setAudioOpened1(false);
        setAudioOpened2(false);
        setAudioOpened3(false);
        setAudioOpened4(false);
        setAudioOpened5(false);
        setAudioOpened6(false);
        func(value);
    };

    const formatOverallTime = () => {
        if (availableGame) {
            const time = parseInt(availableGame.ended ?? '0') - parseInt(availableGame.started ?? '0');
            const penalties = (availableGame.penalties || 0) * 60;
            return formatTime(time + penalties);
        }
    };

    const formatGameTime = () => {
        if (availableGame) {
            const time = parseInt(availableGame.ended ?? '0') - parseInt(availableGame.started ?? '0');
            return formatTime(time);
        }
    };

    const formatPenaltiesTime = () => {
        if (availableGame) {
            return formatTime((availableGame.penalties || 0) * 60);
        }
    };

    const formatTime = (time: number) => {
        const { getSeconds, getMinutes, getHours } = getTimeSplit(time);

        return (
            <div className="zdar-ending-time-wrapper">
                <div className="zdar-ending-time-part">
                    <div>{getHours}h</div>
                </div>
                <div className="zdar-ending-time-delimiter">{':'}</div>
                <div className="zdar-ending-time-part">
                    <div>{getMinutes}m</div>
                </div>
                <div className="zdar-ending-time-delimiter">{':'}</div>
                <div className="zdar-ending-time-part">
                    <div>{getSeconds}s</div>
                </div>
            </div>
        );
    };

    const renderSocial = () => {
        return (
            <div className="zdar-social__wrapper">
                <div className="zdar-social__text">Více o dalších dobrodružstvích od Šifrovky naleznete zde:</div>
                <div className="zdar-social__icons">
                    <a href="https://www.facebook.com/sifrovka'" target="_blank" rel="noreferrer">
                        <div className="social-icon__wrapper social-icon__wrapper--facebook">
                            <img src={facebook} alt="facebook" width={50} height={50} />
                        </div>
                    </a>
                    <a href="https://www.instagram.com/sifrovka/" target="_blank" rel="noreferrer">
                        <div className="social-icon__wrapper social-icon__wrapper--instagram">
                            <img src={instagram} alt="instagram" width={46} height={46} />
                        </div>
                    </a>
                    <a href="https://sifrovka.cz/" target="_blank" rel="noreferrer">
                        <div className="social-icon__wrapper social-icon__wrapper--website">
                            <img src={website} alt="website" width={26} height={26} />
                        </div>
                    </a>
                </div>
            </div>
        );
    };

    const onClick = () => {
        setPage(2);
        window.scrollTo(0, 0);
    };

    if (isEmptyObject(stepContent)) {
        return (
            <>
                <GameWrapper>
                    <div className="card game-card">
                        <Loading color="#23ab70" />
                    </div>
                </GameWrapper>
            </>
        );
    }

    if (availableGame) {
        const lastStep = availableGame.lastStep;
        if (lastStep === null || (lastStep && props.currentStep < lastStep)) {
            return (
                <>
                    <GameWrapper>
                        <div className="card game-card">
                            <Loading color="#23ab70" />
                        </div>
                    </GameWrapper>
                </>
            );
        }
    }

    return (
        <div className="card game-card">
            {page === 1 && (
                <>
                    {props.title && (
                        <ZdarTaskTitle
                            text={props.title}
                            audioOpened={audioOpened1}
                            setAudioOpened={() => onOpenAudio(true, setAudioOpened1)}
                            audio={audio[0]}
                        />
                    )}
                    {audio[0] && <GameAudioPlayer track={audio[0]} opened={audioOpened1} />}
                    {taskText && (
                        <ZdarTaskText
                            text={taskText}
                            audio={audio[0]}
                            audioOpened={audioOpened1}
                            setAudioOpened={() => onOpenAudio(true, setAudioOpened1)}
                        />
                    )}
                    <ImageWrapper src={image} alt="erb" allowLightbox={false} />
                    <div className="zdar-ending-score zdar-ending-score__first">
                        <div className={props.title ? 'task-text' : 'task-text task-text--standalone'}>
                            <div className="zdar-ending-time-title">Váš celkový čas:</div>
                            {formatOverallTime()}
                        </div>
                    </div>
                    <div className="zdar-ending-score">
                        <div className={props.title ? 'task-text' : 'task-text task-text--standalone'}>
                            <div className="zdar-ending-time-title">Váš herní čas:</div>
                            {formatGameTime()}
                        </div>
                    </div>
                    <div className="zdar-ending-score zdar-ending-score__last">
                        <div className={props.title ? 'task-text' : 'task-text task-text--standalone'}>
                            <div className="zdar-ending-time-title">Penalizace:</div>
                            {formatPenaltiesTime()}
                        </div>
                    </div>
                    <div className={props.title ? 'task-text' : 'task-text task-text--standalone'}>
                        Pokud vás zajímá se o erbu dozvědět více, pokračujte dolů dál po této stránce. <br />
                        <br /> Pokud si přejete ukončit hru, klikněte na tlačítko Dále.
                    </div>
                    <ContinueButton text="Dále" onClick={onClick} />
                    <div className="zdar-ending-coat-title">O erbu</div>
                    {audio[2] && <GameAudioPlayer track={audio[2]} opened={audioOpened3} />}
                    {coatTexts && (
                        <ZdarTaskText
                            text={coatTexts[0]}
                            audio={audio[2]}
                            audioOpened={audioOpened3}
                            setAudioOpened={() => onOpenAudio(true, setAudioOpened3)}
                            emptyImage={true}
                        />
                    )}
                    {coatImages && <ImageWrapper src={coatImages[0]} alt="erb" allowLightbox={false} />}
                    {audio[3] && <GameAudioPlayer track={audio[3]} opened={audioOpened4} />}
                    {coatTexts && (
                        <ZdarTaskText
                            text={coatTexts[1]}
                            audio={audio[3]}
                            audioOpened={audioOpened4}
                            setAudioOpened={() => onOpenAudio(true, setAudioOpened4)}
                            emptyImage={true}
                        />
                    )}
                    {coatImages && <ImageWrapper src={coatImages[1]} alt="erb" allowLightbox={false} />}
                    {audio[4] && <GameAudioPlayer track={audio[4]} opened={audioOpened5} />}
                    {coatTexts && (
                        <ZdarTaskText
                            text={coatTexts[2]}
                            audio={audio[4]}
                            audioOpened={audioOpened5}
                            setAudioOpened={() => onOpenAudio(true, setAudioOpened5)}
                            emptyImage={true}
                        />
                    )}
                    {coatImages && <ImageWrapper src={coatImages[2]} alt="erb" allowLightbox={false} />}
                    {audio[5] && <GameAudioPlayer track={audio[5]} opened={audioOpened6} />}
                    {coatTexts && (
                        <ZdarTaskText
                            text={coatTexts[3]}
                            audio={audio[5]}
                            audioOpened={audioOpened6}
                            setAudioOpened={() => onOpenAudio(true, setAudioOpened6)}
                            emptyImage={true}
                        />
                    )}
                    <div className="big-margin" />
                    <div className={props.title ? 'task-text' : 'task-text task-text--standalone'}>
                        Pokud si přejete ukončit hru, klikněte na tlačítko Dále.
                    </div>
                    <ContinueButton text="Dále" onClick={onClick} />
                </>
            )}
            {page === 2 && (
                <>
                    {props.title && (
                        <ZdarTaskTitle
                            text={'Děkujeme'}
                            audioOpened={audioOpened2}
                            setAudioOpened={() => onOpenAudio(true, setAudioOpened2)}
                            audio={audio[1]}
                        />
                    )}
                    {audio[1] && <GameAudioPlayer track={audio[1]} opened={audioOpened2} />}
                    {taskText2 && (
                        <ZdarTaskText
                            text={taskText2}
                            audio={audio[1]}
                            audioOpened={audioOpened2}
                            setAudioOpened={() => onOpenAudio(true, setAudioOpened2)}
                        />
                    )}
                    <ImageWrapper src={cat} alt="cat" width={120} />
                    {renderSocial()}
                    <div className="zdar-ending-logo">
                        <a href="https://sifrovka.cz/" target="_blank" rel="noreferrer">
                            <ImageWrapper src={logo} alt="logo" />
                        </a>
                    </div>
                    <div className="task-text">{parse(giftCertificateText)}</div>
                    <div className="zdar-ending-thankyou-title">{'Poděkování'}</div>
                    <div className="task-text">{parse(thankYouText)}</div>
                </>
            )}
        </div>
    );
}
